import { useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import { fetchActionLogs } from '../../api/custom-actions'
import { formatDatetimeInTimezone } from '../../utils/timezones'
import { useAuthStore } from '../../stores/auth.store'
import { useApi } from '../../api/client'
import { useToastStore } from '../../stores/toast.store'
import type { ActionLogWithUser } from '@crm/shared'

interface ObjectItem {
  id: string
  name: string
  label: string
}

export function ActionLogs() {
  const timezone = useAuthStore((s) => s.user?.timezone) ?? 'UTC'
  const toast = useToastStore()
  const [logs, setLogs] = useState<ActionLogWithUser[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [cursor, setCursor] = useState<string | null>(null)
  const [filterObjectId, setFilterObjectId] = useState('')
  const [filterActionId, setFilterActionId] = useState('')

  const { data: objectsData } = useApi<{ data: ObjectItem[] }>('/objects')
  const objects = objectsData?.data ?? []

  const { data: actionsData } = useApi<{ data: { id: string; name: string; label: string; object_id: string }[] }>('/settings/custom-actions')
  const customActions = actionsData?.data ?? []

  const filteredActions = filterObjectId
    ? customActions.filter((a) => a.object_id === filterObjectId)
    : customActions

  const loadLogs = useCallback(async (cursorParam?: string) => {
    setLoading(true)
    try {
      const result = await fetchActionLogs({
        cursor: cursorParam,
        limit: 50,
        object_id: filterObjectId || undefined,
        custom_action_id: filterActionId || undefined,
      })
      const newLogs = result.data ?? []
      setLogs((prev) => cursorParam ? [...prev, ...newLogs] : newLogs)
      setHasMore(result.pagination?.has_more ?? false)
      setCursor(result.pagination?.next_cursor ?? null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载操作日志失败')
    } finally {
      setLoading(false)
    }
  }, [filterObjectId, filterActionId])

  const loadMore = useCallback(() => {
    if (cursor) loadLogs(cursor)
  }, [cursor, loadLogs])

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Action Logs</Typography>
        <Button variant="outlined" size="small" onClick={() => loadLogs()} disabled={loading}>
          {logs.length === 0 ? 'Load Logs' : 'Refresh'}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Object</InputLabel>
          <Select
            value={filterObjectId}
            onChange={(e) => {
              setFilterObjectId(e.target.value)
              setFilterActionId('')
            }}
            label="Object"
          >
            <MenuItem value="">All</MenuItem>
            {objects.map((obj) => (
              <MenuItem key={obj.id} value={obj.id}>{obj.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Action</InputLabel>
          <Select
            value={filterActionId}
            onChange={(e) => setFilterActionId(e.target.value)}
            label="Action"
          >
            <MenuItem value="">All</MenuItem>
            {filteredActions.map((action) => (
              <MenuItem key={action.id} value={action.id}>{action.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {logs.length === 0 && !loading && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary' }}>Click "Load Logs" to view action click history</Typography>
        </Paper>
      )}

      {logs.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Object</TableCell>
                <TableCell>Record ID</TableCell>
                <TableCell>Clicked At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => {
                const obj = objects.find((o) => o.id === log.object_id)
                return (
                  <TableRow key={log.id} hover>
                    <TableCell>{log.display_name || log.user_id}</TableCell>
                    <TableCell>{log.action_label}</TableCell>
                    <TableCell>
                      <Chip
                        label={log.action_type === 'qr_code' ? 'QR Code' : 'Share'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{obj?.label ?? log.object_id}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{log.record_id}</TableCell>
                    <TableCell>{formatDatetimeInTimezone(log.clicked_at, timezone)}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {hasMore && !loading && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button size="small" onClick={loadMore}>Load More</Button>
        </Box>
      )}
    </Box>
  )
}
